// RAM-215 built this as pure decision logic for a mandatory, blocking license gate. Per direct
// product-owner direction (2026-08-08, "non-blocking banner"), the blocking gate is REMOVED --
// this file now computes a STATUS/SEVERITY value for a persistent, dismissible status banner
// instead of an allow/block decision. Deliberately kept JSX-free/dependency-free (same reasoning
// as before: exercised directly by a plain script/test runner, no rendering or network deps) --
// only the shape of what it returns changed, not that discipline.
//
// GET /license/status returns one of two shapes (see LicenseManagement.tsx):
//   - StubLicenseStatus: pre-RAM-187, returned byte-identically whenever no real license has
//     ever been installed. Has no `license_id` field -- that absence is the discriminator.
//   - RealLicenseStatus: a real signed license was installed at least once. Always has
//     `license_id`, plus `status` (LicenseState, license_context.py) and `tenant_id`.
// This file only reads the two fields it needs (`status`, `tenant_id`) plus the discriminator,
// so it takes the raw response as a loosely-typed record rather than importing either interface
// from LicenseManagement.tsx -- keeps this file dependency-free.
export type LicenseStatusPayload = Record<string, unknown>;

// The three "something's wrong" categories, unchanged from RAM-215's block-decision reasons --
// reused verbatim as banner content categories per the product owner's explicit instruction, not
// reinvented. `'ok'` is the new fourth state (no license problem worth surfacing) that replaces
// the old `blocked: false` case.
export type LicenseGateReason = 'not_installed' | 'invalid_status' | 'tenant_mismatch';
export type LicenseBannerStatus = 'ok' | LicenseGateReason;

export interface LicenseBannerOutcome {
  status: LicenseBannerStatus;
  /** Only set for `invalid_status` -- the actual LicenseState string (EXPIRED, SUSPENDED, ...). */
  rawStatus?: string;
}

// LicenseState enum, verbatim (license_context.py). Deliberately NOT a TS `enum` -- string-union
// + Set keeps this file free of any construct a plain type-stripping runtime can't erase, and
// costs nothing since these values are never used as a runtime dictionary of the enum itself.
const BROKEN_STATUSES = new Set([
  'EXPIRED', 'INVALID', 'NOT_INSTALLED', 'DEPLOYMENT_MISMATCH', 'SUSPENDED', 'RECOVERY',
]);
// PERMISSIVE_STATES, license_context.py -- GRACE is included deliberately (keeps *existing*
// entitled packages running; this UI follows every existing backend consumer's own treatment of
// GRACE as usable, per RAM-215's design notes, still true now that this is a banner not a gate).
const PERMISSIVE_STATUSES = new Set(['ACTIVE', 'GRACE']);

// Duplicated from Layout.tsx's own ADMIN_TENANT_ROLES rather than imported -- Layout.tsx renders
// LicenseStatusBanner, so importing back from here would be a circular import. Same precedent
// LICENSE_BANNER_DISMISS_KEY's own comment (LicenseStatusBanner.tsx) already establishes for this
// exact file pair: keep both in sync if this ever changes.
const ADMIN_TENANT_ROLES = new Set(['TenantAdmin', 'DataEngineer', 'Operations', 'Manager']);

export interface ComputeLicenseGateInput {
  /** undefined covers "not signed in yet" -- the hook only ever calls this once authenticated. */
  authSource: 'local' | 'ciam' | undefined;
  /** AuthUser.tenantName -- only ever set for authSource === 'ciam'. */
  tenantName?: string;
  /** AuthUser.tenantRole -- only ever set for authSource === 'ciam'. */
  tenantRole?: string;
  /** Raw GET /license/status body, or undefined if not yet fetched. */
  data: LicenseStatusPayload | undefined;
  isLoading: boolean;
  isError: boolean;
}

// require_license_tenant_match's exact wording (dependencies.py:507) -- reused verbatim so the
// UI-side and server-side enforcement points never drift into two different messages for the
// same fact.
export const TENANT_MISMATCH_MESSAGE = 'This deployment is licensed for a different tenant.';

export function computeLicenseBannerStatus(input: ComputeLicenseGateInput): LicenseBannerOutcome {
  const { authSource, tenantName, tenantRole, data, isLoading, isError } = input;

  // Local admin is this system's one deliberate cross-tenant/platform-operator identity --
  // NEVER shown a license banner, matching require_license_tenant_match's own
  // `auth_source == "local"` bypass and the (now non-blocking, but still true) design rule that
  // the bootstrap admin's session is never license-gated in any form.
  if (authSource === 'local') return { status: 'ok' };

  // 2026-08-21: a minimal-nav (self-service-API-only) tenant role has exactly one page -- My API
  // Access -- and no ability to act on a license problem (installing one is an admin action).
  // Showing "Get licensed" here is pure noise for that identity, found live on a real
  // ApiDataIntegrator session. Same isMinimalNav concept Layout.tsx's own nav already uses.
  if (authSource === 'ciam' && tenantRole && !ADMIN_TENANT_ROLES.has(tenantRole)) return { status: 'ok' };

  // Fail-safe: a transient outage or an in-flight request must never LOOK LIKE a license problem
  // -- same fail-open-on-uncertainty posture license_client.py documents for itself ("a transient
  // outage must never look like no license installed"). This only covers "we don't know yet" --
  // once a real status value below is actually in hand, it is reported as-is, even if it's bad
  // news.
  // 2026-08-21: `!data` alone doesn't cover every "not a real payload" case -- found live, a
  // routing gap made GET /license/status return a real 200 whose body was an unrelated HTML page,
  // and axios handed that raw string through as `data` (truthy, so `!data` didn't catch it). The
  // `in` operator throws on a non-object, which white-screened the whole app post-login. Guard
  // the type explicitly -- a malformed "successful" response must degrade to the same safe 'ok'
  // fallback as a real error, never crash the render.
  if (isLoading || isError || !data || typeof data !== 'object') return { status: 'ok' };

  if (!('license_id' in data)) {
    return { status: 'not_installed' };
  }

  const status = String(data.status ?? '');

  if (BROKEN_STATUSES.has(status)) {
    return { status: 'invalid_status', rawStatus: status };
  }

  if (PERMISSIVE_STATUSES.has(status)) {
    const licensedTenant = typeof data.tenant_id === 'string' ? data.tenant_id : undefined;
    if (tenantName && licensedTenant && tenantName !== licensedTenant) {
      return { status: 'tenant_mismatch' };
    }
    return { status: 'ok' };
  }

  // LicenseState is meant to be exhaustive (8 values, all covered by the two sets above) -- an
  // unrecognized status string here means either a future backend state this file hasn't been
  // taught yet, or a malformed response. Per this codebase's own "fail LOUD, don't silently
  // swallow" discipline (see MEMORY: AI self-heal over patching), still surface this as a banner
  // problem rather than silently treating an unknown state as fine.
  return { status: 'invalid_status', rawStatus: status };
}
